// Brand-colour SVG icons for third-party providers. Lucide doesn't ship brand
// marks — these are the official multi-colour glyphs used wherever we surface a
// provider identity (calendar source list, source-of-truth chip on work items
// derived from email, etc.).

interface BrandIconProps {
  /** Pixel size for both width + height. Defaults to 18. */
  size?: number;
  className?: string;
}

export function MicrosoftIcon({ size = 18, className }: BrandIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 21 21" fill="none" className={className} aria-hidden>
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

export function GoogleIcon({ size = 18, className }: BrandIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden>
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

/**
 * Map a URL or hostname-fragment to the brand it represents. Used by surfaces
 * that store a `Source: https://...` reference and want to render the right
 * provider chip without a hard-coded provider field.
 */
export function brandForUrl(url: string): 'microsoft' | 'google' | null {
  const u = url.toLowerCase();
  if (u.includes('outlook.office.com')
      || u.includes('outlook.office365.com')
      || u.includes('outlook.live.com')
      || u.includes('graph.microsoft.com')
      || u.includes('teams.microsoft.com')) {
    return 'microsoft';
  }
  if (u.includes('mail.google.com')
      || u.includes('calendar.google.com')
      || u.includes('drive.google.com')
      || u.includes('docs.google.com')
      || u.includes('meet.google.com')) {
    return 'google';
  }
  return null;
}

/** Human label for the brand a URL belongs to. */
export function brandLabel(brand: 'microsoft' | 'google'): string {
  return brand === 'microsoft' ? 'Outlook' : 'Google';
}
